import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    InputSignal,
    signal,
    WritableSignal,
} from '@angular/core';
import turfDistance from '@turf/distance';
import { LngLat, Map as MapLibreMap } from 'maplibre-gl';

import { TileSourceTypeEnum } from '@connect-services/client/services/basemap.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

enum SourceTypeEnum {
    SATELLITE_VIEW = 'satellite-view',
    DEFAULT_VIEW = 'default-view',
}

const MAPLIBRE_ATTRIB_SELECTOR = '.maplibregl-ctrl-attrib';
const SCALE_MARGIN_OFFSET = 20;
const SCALE_WIDTH = 100;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-map-scale-controller',
    styleUrl: './map-scale-controller.component.scss',
    templateUrl: './map-scale-controller.component.html',
})
export class MapScaleControllerComponent {
    public readonly backgroundSource: InputSignal<TileSourceTypeEnum> = input.required<TileSourceTypeEnum>();
    protected readonly customScaleStyle = computed(() => {
        return this.backgroundSource() === TileSourceTypeEnum.SATELLITE
            ? SourceTypeEnum.SATELLITE_VIEW
            : SourceTypeEnum.DEFAULT_VIEW;
    });
    protected readonly scaleLabel: WritableSignal<string> = signal<string>('');
    private map?: MapLibreMap;

    public setMap(map: MapLibreMap): void {
        this.map = map;

        this.buildScaleController();
    }

    private attachMapLibreIconListener(): boolean {
        const mapLibreIconContainer: HTMLDetailsElement | null = document.querySelector(MAPLIBRE_ATTRIB_SELECTOR);

        if (!mapLibreIconContainer) {
            return false;
        }

        this.setScaleContainerPosition(mapLibreIconContainer);

        const resizeObserver = new ResizeObserver((): void => this.setScaleContainerPosition(mapLibreIconContainer));
        resizeObserver.observe(mapLibreIconContainer);

        return true;
    }

    private buildScaleController(): void {
        this.map!.on('moveend', () => {
            this.updateScaleLabel();
        });

        this.setScaleBarWidth();
        this.initializeMapLibreObserver();
    }

    private initializeMapLibreObserver(): void {
        const domObserver = new MutationObserver(() => {
            if (!this.attachMapLibreIconListener()) {
                return;
            }
            domObserver.disconnect();
        });

        domObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    private setScaleBarWidth(): void {
        const scaleBar: HTMLElement | null = document.getElementById('custom-scale');

        if (!scaleBar) {
            return;
        }

        scaleBar.style.width = `${SCALE_WIDTH}px`;
    }

    private setScaleContainerPosition(mapLibreIconContainer: HTMLDetailsElement): void {
        const mapLibreIconDimensions = mapLibreIconContainer.getBoundingClientRect();
        const scaleContainer = document.getElementById('custom-scale-container');

        if (!scaleContainer) {
            return;
        }

        scaleContainer.style.marginLeft = `${mapLibreIconDimensions.width + SCALE_MARGIN_OFFSET}px`;
    }

    private updateScaleLabel(): void {
        const start: LngLat = this.map!.unproject([0, 0]);
        const end: LngLat = this.map!.unproject([SCALE_WIDTH, 0]);

        const distance: number = turfDistance([start.lng, start.lat], [end.lng, end.lat], { units: 'kilometers' });

        const label: string = distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(2)} km`;

        this.scaleLabel.set(label);
    }
}
